import { TZDate } from '@date-fns/tz';
import { isMatch, isBefore, isAfter, startOfDay, getYear, differenceInDays, parse, isValid, format } from 'date-fns';
import { nl } from 'date-fns/locale';

import { Injectable } from '@angular/core';
@Injectable()
export class ApDateService {
    public static dateSeparators = /[\\/:,_.\-–—]/;
    public static timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    public static utc(date?: Date): Date {
        // if the date is null, it should be returned, only undefined should set a new date.
        if (date === null) {
            return null;
        }

        return new TZDate(date ? date : new Date(), ApDateService.timezone);
    }
    public static realTimeFormat(valueInput: string) {
        if (!valueInput) {
            return valueInput;
        }
        let value = valueInput;
        //set the day to correct format
        if (value.length === 2 && value.charAt(0)) {
            if (!isNaN(+value.charAt(1))) {
                value += '-';
            } else {
                value = `0${value.charAt(0)}-`;
            }
        } else if (value.length === 5 && value.charAt(3)) {
            //set the month to correct format
            if (!isNaN(+value.charAt(4))) {
                value = `${value.slice(0, 2)}-${value.slice(3)}-`;
            } else {
                value = `${value.slice(0, 2)}-0${value.charAt(3)}-`;
            }
        }
        value = value.replace(/\//g, '-').replace(/\\/g, '-'); //replace any escaped charaters with -

        //replace any double --
        if (/--$/.test(value)) {
            while (value.indexOf('--', 0) > -1) {
                value = value.replace(/--/g, '-');
            }
        }

        return value;
    }

    public static format(date: any, displayFormat?: string): string {
        if (!(date instanceof Date)) {
            return date;
        }
        return format(date, displayFormat ? displayFormat : 'dd-MM-yyyy', { locale: nl });
    }

    public static formatRelative(dateToFormat: any, stripDate = false): string {
        if (!(dateToFormat instanceof Date)) {
            return dateToFormat;
        }

        const date = startOfDay(dateToFormat);
        const today = new TZDate(startOfDay(new Date()), ApDateService.timezone);
        const diff = differenceInDays(date, today);

        switch (diff) {
            case 1:
                return stripDate ? 'Morgen' : `Morgen - ${ApDateService.formatRecent(date)}`;
            case 0:
                return stripDate ? 'Vandaag' : `Vandaag - ${ApDateService.formatRecent(date)}`;
            case -1:
                return stripDate ? 'Gisteren' : `Gisteren - ${ApDateService.formatRecent(date)}`;
            default:
                return ApDateService.formatRecent(date);
        }
    }

    public static formatRecent(date: any): string {
        if (!(date instanceof Date)) {
            return date;
        }

        const currentYear = getYear(new TZDate(new Date(), ApDateService.timezone));
        const formatToUse = getYear(date) === currentYear ? 'd MMMM' : 'd MMMM yyyy';
        return format(date, formatToUse, { locale: nl });
    }

    public static formatLong(date: any): string {
        if (!(date instanceof Date)) {
            return date;
        }
        return format(date, 'd MMMM yyyy', { locale: nl });
    }

    public static parse(value: any): Date {
        const parseFormats = ['d M yyyy', 'dMyyyy'];
        let valueParsed = value;
        if (valueParsed && typeof valueParsed === 'string') {
            // change all separators to spaces and remove additional spacing
            valueParsed = valueParsed
                .replace(new RegExp(ApDateService.dateSeparators, 'g'), ' ')
                .replace(/\s+/g, ' ')
                .match(/\d{0,2}\s?\d{0,2}\s?\d{0,4}/)[0];

            // check if display format is a match
            let match;
            let lastFormat;
            for (const f of parseFormats) {
                if (isMatch(valueParsed, f, { locale: nl })) {
                    match = f;
                }
                lastFormat = f;
            }

            // if there is no match, we'll try the lastFormat, if incorrect it will return invalid date.
            if (!match) {
                return parse(valueParsed, lastFormat, new Date());
            }

            // parse date to matched found format
            const parsed = parse(valueParsed, match, new Date());

            // if year does not have 4 digits we'll return invalid date.
            if (getYear(parsed).toString().length !== 4) {
                return new Date('Invalid date'); //will always return invalid date
            }

            return new TZDate(parsed, ApDateService.timezone);
        }
        return valueParsed ? new TZDate(valueParsed, ApDateService.timezone) : valueParsed;
    }

    public static clampDate(date: Date, min?: Date | null, max?: Date | null): Date {
        if (min && isBefore(date, min)) {
            return min;
        }
        if (max && isAfter(date, max)) {
            return max;
        }
        return date;
    }

    public static getValidDateOrNull(obj: any): Date | null {
        return obj instanceof Date && isValid(obj) ? obj : null;
    }
}
